import { useState, useEffect } from 'react';
import { getUserActivities, saveActivity } from '../services/firebase';
import { Activity } from '../types/activity';
import { sampleActivities } from '../data/sampleActivities';
import { useAuth } from './useAuth';

function useActivities(userId: string | null) {
  const { isGuest } = useAuth();
  const [activities, setActivities] = useState<Activity[]>(() => {
    if (isGuest) {
      const savedActivities = localStorage.getItem('guestActivities');
      if (!savedActivities || 
          savedActivities.includes('Emma') || 
          savedActivities.includes('Jack') || 
          savedActivities.includes('Sophie')) {
        localStorage.setItem('guestActivities', JSON.stringify(sampleActivities));
        return sampleActivities;
      }
      return JSON.parse(savedActivities);
    }
    return [];
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isGuest) {
      localStorage.setItem('guestActivities', JSON.stringify(activities));
    }
  }, [activities, isGuest]);

  useEffect(() => {
    const loadActivities = async () => {
      try {
        console.log('Loading activities');
        console.log('Current user ID:', userId);
        console.log('Guest mode:', isGuest);

        if (!isGuest && userId) {
          try {
            const userActivities = await getUserActivities(userId);
            console.log(`Loaded ${userActivities.length} activities`);
            setActivities(userActivities.length > 0 ? userActivities : sampleActivities);
            setError(null);
          } catch (err) {
            console.error('Error loading activities:', err);
            setActivities(sampleActivities);
            setError('Unable to load activities. Using sample data instead.');
          }
        }
      } catch (loadError) {
        console.error('Error loading activities:', loadError);
        setError('Failed to load activities');
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, [userId, isGuest]);

  const addActivity = async (activityData: Omit<Activity, 'id'>) => {
    try {
      console.log('Adding activity');
      if (isGuest) {
        const newActivity = {
          ...activityData,
          id: Math.random().toString(36).substring(2)
        };
        setActivities(prev => [newActivity, ...prev]);
        return newActivity.id;
      } else if (userId) {
        try {
          const activityId = await saveActivity(userId, activityData);
          console.log(`Added activity with ID ${activityId}`);
          const newActivity = { ...activityData, id: activityId };
          setActivities(prev => [newActivity, ...prev]);
          return activityId;
        } catch (saveError) {
          console.error('Error saving activity:', saveError);
          throw saveError;
        }
      }
    } catch (addError) {
      console.error('Error adding activity:', addError);
      throw addError;
    }
  };

  const updateActivity = async (activity: Activity) => {
    try {
      console.log('Updating activity');
      if (isGuest) {
        setActivities(prev => 
          prev.map(a => a.id === activity.id ? activity : a)
        );
      } else if (userId) {
        try {
          await saveActivity(userId, activity);
          console.log(`Updated activity with ID ${activity.id}`);
          setActivities(prev =>
            prev.map(a => a.id === activity.id ? activity : a)
          );
        } catch (saveError) {
          console.error('Error saving activity:', saveError);
          throw saveError;
        }
      }
    } catch (updateError) {
      console.error('Error updating activity:', updateError);
      throw updateError;
    }
  };

  const deleteActivity = async (id: string) => {
    try {
      console.log('Deleting activity');
      if (isGuest) {
        setActivities(prev => prev.filter(a => a.id !== id));
      } else if (userId) {
        try {
          await saveActivity(userId, { id, deleted: true } as Partial<Activity>);
          console.log(`Deleted activity with ID ${id}`);
          setActivities(prev => prev.filter(a => a.id !== id));
        } catch (saveError) {
          console.error('Error saving activity:', saveError);
          throw saveError;
        }
      }
    } catch (deleteError) {
      console.error('Error deleting activity:', deleteError);
      throw deleteError;
    }
  };

  return { 
    activities, 
    loading, 
    error, 
    addActivity, 
    updateActivity, 
    deleteActivity 
  };
}

export { useActivities };